import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, Upload, X } from 'lucide-react';
import { parseSpreadsheetMlRows } from '../../../placeholders/OrganizationsBulkImportModal';

const escapeXml = (value = '') =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const detectDelimiter = (line = '') => {
  const commas = (line.match(/,/g) || []).length;
  const semis = (line.match(/;/g) || []).length;
  const tabs = (line.match(/\t/g) || []).length;
  if (tabs >= commas && tabs >= semis && tabs > 0) return '\t';
  if (semis >= commas) return ';';
  return ',';
};

const splitCsvLine = (line, delimiter) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else inQuotes = !inQuotes;
      continue;
    }
    if (ch === delimiter && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
};

const normalizeHeader = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const looksLikeSpreadsheetMl = (text = '') => {
  const sample = String(text || '').slice(0, 4000).toLowerCase();
  return (
    sample.includes('mso-application') ||
    sample.includes('urn:schemas-microsoft-com:office:spreadsheet') ||
    (sample.includes('<workbook') && sample.includes('<worksheet'))
  );
};

const looksLikeBinaryOffice = (text = '') => {
  const sample = String(text || '');
  if (!sample) return false;
  if (sample.charCodeAt(0) === 0x50 && sample.charCodeAt(1) === 0x4b) return true;
  return [...sample.slice(0, 2000)].filter((ch) => ch.charCodeAt(0) === 0).length > 20;
};

const parseGenericCsv = (rawText, headers, aliases, requiredField, requiredLabel) => {
  const text = String(rawText || '').replace(/^\uFEFF/, '').trim();
  if (!text) return { rows: [], error: 'Pega o sube un archivo CSV o la plantilla Excel (.xls).' };
  if (looksLikeBinaryOffice(text)) {
    return {
      rows: [],
      error: 'Archivo binario. Usa la plantilla .xls o exporta a CSV (;).',
    };
  }
  let table;
  if (looksLikeSpreadsheetMl(text)) {
    table = parseSpreadsheetMlRows(text);
  } else {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return { rows: [], error: 'Archivo vacío.' };
    const delimiter = detectDelimiter(lines[0]);
    table = lines.map((line) => splitCsvLine(line, delimiter));
  }
  if (!table.length) return { rows: [], error: 'Sin filas.' };
  const mapHeader = (header) => {
    const key = normalizeHeader(header);
    for (const [field, list] of Object.entries(aliases)) {
      if (list.includes(key)) return field;
    }
    return headers.includes(key) ? key : null;
  };
  const mapping = (table[0] || []).map(mapHeader);
  if (!mapping.includes(requiredField)) {
    return { rows: [], error: `Falta la columna ${requiredLabel}.` };
  }
  const empty = () => headers.reduce((acc, k) => ({ ...acc, [k]: '' }), {});
  const rows = [];
  for (let i = 1; i < table.length; i += 1) {
    const cells = table[i] || [];
    if (!cells.some((c) => String(c || '').trim())) continue;
    const row = empty();
    mapping.forEach((field, idx) => {
      if (field) row[field] = String(cells[idx] || '').trim();
    });
    if (!row[requiredField]) continue;
    rows.push(row);
  }
  if (!rows.length) return { rows: [], error: `Sin filas con ${requiredLabel}.` };
  return { rows, error: '' };
};

const ENTITY_HEADERS_BASE = [
  'name',
  'business_name',
  'rut',
  'entity_type',
  'email',
  'phone',
  'address',
  'commune',
  'region',
  'country',
  'funnel_stage',
  'status',
  'target_amount',
  'committed_amount',
  'created_by',
];

const ENTITY_ALIASES = {
  name: ['name', 'nombre'],
  business_name: ['business_name', 'razon_social'],
  rut: ['rut'],
  entity_type: ['entity_type', 'tipo', 'tipo_inversionista', 'tipo_fondo'],
  email: ['email', 'correo'],
  phone: ['phone', 'telefono', 'fono'],
  address: ['address', 'direccion'],
  commune: ['commune', 'comuna'],
  region: ['region'],
  country: ['country', 'pais'],
  funnel_stage: ['funnel_stage', 'etapa', 'pipeline'],
  status: ['status', 'estado'],
  target_amount: ['target_amount', 'monto_objetivo', 'monto'],
  committed_amount: ['committed_amount', 'monto_comprometido'],
  agency: ['agency', 'agencia', 'institucion'],
  instrument: ['instrument', 'instrumento'],
  application_deadline: ['application_deadline', 'fecha_cierre', 'plazo'],
  created_by: ['created_by', 'creado_por'],
};

export const downloadFinancingEntitiesTemplate = ({ trackKey = 'investors', sheetName = 'Entidades' } = {}) => {
  const isFund = trackKey === 'public_funds';
  const headers = isFund
    ? [...ENTITY_HEADERS_BASE.slice(0, -1), 'agency', 'instrument', 'application_deadline', 'created_by']
    : ENTITY_HEADERS_BASE;
  const example = isFund
    ? [
        'CORFO ScaleUp',
        'CORFO',
        '61.797.030-3',
        'Concurso / convocatoria',
        'contacto@corfo.cl',
        '',
        'Moneda 1123',
        'Santiago',
        'Metropolitana',
        'Chile',
        'Identificado',
        'Activo',
        '50000000',
        '0',
        'CORFO',
        'Subsidio',
        '2026-12-31',
        'sistema',
      ]
    : [
        'Fondo Ejemplo Ventures',
        'Fondo Ejemplo SpA',
        '76.123.456-0',
        'Venture capital',
        'contacto@ejemplo.cl',
        '+56912345678',
        'Av. Apoquindo 100',
        'Las Condes',
        'Metropolitana',
        'Chile',
        'Identificado',
        'Activo',
        '100000000',
        '0',
        'sistema',
      ];
  const headerCells = headers.map((h) => `<Cell><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`).join('');
  const exampleCells = example.map((c) => `<Cell><Data ss:Type="String">${escapeXml(c)}</Data></Cell>`).join('');
  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Worksheet ss:Name="${escapeXml(sheetName)}">
  <Table>
   <Row>${headerCells}</Row>
   <Row>${exampleCells}</Row>
  </Table>
 </Worksheet>
</Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = isFund ? 'plantilla_fondos_publicos.xls' : 'plantilla_inversionistas.xls';
  a.click();
  URL.revokeObjectURL(url);
};

export const downloadFinancingContactsTemplate = ({ parentLabel = 'Proveedor' } = {}) => {
  const headers = ['full_name', 'role', 'email', 'phone', 'parent_name', 'status', 'created_by'];
  const example = ['Ana Pérez', 'Inversiones', 'ana@ejemplo.cl', '+56911112222', 'Fondo Ejemplo Ventures', 'Activo', 'sistema'];
  const headerCells = headers.map((h) => `<Cell><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`).join('');
  const exampleCells = example.map((c) => `<Cell><Data ss:Type="String">${escapeXml(c)}</Data></Cell>`).join('');
  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Worksheet ss:Name="Contactos">
  <Table>
   <Row>${headerCells}</Row>
   <Row>${exampleCells}</Row>
  </Table>
 </Worksheet>
</Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `plantilla_contactos_${String(parentLabel || 'financiamiento')
    .toLowerCase()
    .replace(/\s+/g, '_')}.xls`;
  a.click();
  URL.revokeObjectURL(url);
};

const BulkShell = ({
  isOpen,
  onClose,
  isImporting,
  isDarkMode,
  title,
  subtitle,
  headers,
  labels,
  parseFn,
  onImport,
  result,
  successNoun,
}) => {
  const [step, setStep] = useState('source');
  const [rawText, setRawText] = useState('');
  const [previewRows, setPreviewRows] = useState([]);
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setStep('source');
    setRawText('');
    setPreviewRows([]);
    setLocalError('');
    setLocalSuccess('');
  }, [isOpen]);

  const colors = {
    text: isDarkMode ? '#f4f4f5' : '#0f172a',
    muted: isDarkMode ? '#a1a1aa' : '#64748b',
    border: isDarkMode ? '#3f3f46' : '#e2e8f0',
    inputBg: isDarkMode ? '#18181b' : '#fff',
    inputBorder: isDarkMode ? '#3f3f46' : '#cbd5e1',
    panel: isDarkMode ? '#09090b' : '#fff',
  };

  const parsed = useMemo(() => parseFn(rawText), [rawText, parseFn]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!previewRows.length || isImporting || localSuccess) return;
    setLocalError('');
    try {
      const data = await onImport?.(previewRows);
      const created = Number(data?.created || 0);
      const skipped = Number(data?.skipped || 0);
      if (Number(data?.errors || 0) === 0 || created > 0) {
        setLocalSuccess(
          `Importación exitosa: ${created} ${successNoun}` +
            (skipped ? `, ${skipped} omitidos` : '') +
            '.'
        );
      }
    } catch (err) {
      setLocalError(err?.message || 'No se pudo importar.');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 11000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={() => !isImporting && onClose?.()}
    >
      <div
        style={{
          width: 'min(1100px, 100%)',
          maxHeight: '92vh',
          overflow: 'auto',
          background: colors.panel,
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          padding: 16,
          display: 'grid',
          gap: 12,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, color: colors.text, fontSize: 18 }}>
              {step === 'preview' ? 'Revisar importación' : title}
            </h3>
            <p style={{ margin: '4px 0 0', color: colors.muted, fontSize: 13 }}>{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} disabled={isImporting} style={{ border: 'none', background: 'transparent', color: colors.muted, cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        {step === 'source' ? (
          <>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 36,
                padding: '0 12px',
                borderRadius: 8,
                border: `1px solid ${colors.inputBorder}`,
                background: colors.inputBg,
                color: colors.text,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                width: 'fit-content',
              }}
            >
              <Upload size={15} />
              Subir CSV / plantilla .xls
              <input
                type="file"
                accept=".csv,.xls,text/csv,text/plain,application/vnd.ms-excel"
                hidden
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (!file) return;
                  try {
                    setRawText(await file.text());
                    setLocalError('');
                  } catch {
                    setLocalError('No se pudo leer el archivo.');
                  }
                }}
              />
            </label>
            <textarea
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              rows={10}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: 8,
                border: `1px solid ${colors.inputBorder}`,
                background: colors.inputBg,
                color: colors.text,
                padding: 10,
                fontSize: 12,
                fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
              }}
            />
            <div style={{ fontSize: 12, color: colors.muted }}>
              {parsed.error || `${parsed.rows.length} fila(s) detectadas.`}
            </div>
          </>
        ) : (
          <div style={{ overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 8, maxHeight: '58vh' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 900 }}>
              <thead>
                <tr style={{ background: isDarkMode ? '#18181b' : '#f8fafc', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>#</th>
                  {headers.map((key) => (
                    <th key={key} style={{ padding: 8, whiteSpace: 'nowrap' }}>
                      {labels[key] || key}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, index) => (
                  <tr key={`r-${index}`} style={{ borderTop: `1px solid ${colors.border}` }}>
                    <td style={{ padding: 8, color: colors.muted }}>{index + 1}</td>
                    {headers.map((key) => (
                      <td key={key} style={{ padding: 4 }}>
                        <input
                          value={row[key] || ''}
                          onChange={(e) =>
                            setPreviewRows((prev) =>
                              prev.map((r, i) => (i === index ? { ...r, [key]: e.target.value } : r))
                            )
                          }
                          style={{
                            width: 110,
                            height: 28,
                            borderRadius: 6,
                            border: `1px solid ${colors.inputBorder}`,
                            background: colors.inputBg,
                            color: colors.text,
                            padding: '0 6px',
                            fontSize: 12,
                          }}
                        />
                      </td>
                    ))}
                    <td>
                      <button
                        type="button"
                        onClick={() => setPreviewRows((prev) => prev.filter((_, i) => i !== index))}
                        style={{ border: 'none', background: 'transparent', color: colors.muted, cursor: 'pointer' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(localError || localSuccess || result) && (
          <div style={{ padding: 10, borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 13, color: colors.text }}>
            {localSuccess ? <div style={{ color: '#15803d', fontWeight: 600 }}>{localSuccess}</div> : null}
            {localError ? <div style={{ color: '#dc2626' }}>{localError}</div> : null}
            {!localSuccess && result ? (
              <div>
                Creados: <strong>{result.created || 0}</strong> · Omitidos: <strong>{result.skipped || 0}</strong> ·
                Errores: <strong>{result.errors || 0}</strong>
              </div>
            ) : null}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting}
            style={{
              height: 38,
              padding: '0 14px',
              borderRadius: 8,
              border: `1px solid ${colors.inputBorder}`,
              background: 'transparent',
              color: colors.text,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cerrar
          </button>
          {step === 'source' ? (
            <button
              type="button"
              disabled={Boolean(parsed.error) || !parsed.rows.length}
              onClick={() => {
                setPreviewRows(parsed.rows);
                setStep('preview');
                setLocalError('');
              }}
              style={{
                height: 38,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background: !parsed.rows.length || parsed.error ? '#94a3b8' : '#2563eb',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Revisar datos
            </button>
          ) : (
            <button
              type="button"
              disabled={isImporting || Boolean(localSuccess) || !previewRows.length}
              onClick={handleSubmit}
              style={{
                height: 38,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {isImporting ? 'Importando…' : localSuccess ? 'Listo' : `Importar ${previewRows.length}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const FinancingEntitiesBulkImportModal = ({
  isOpen,
  onClose,
  onImport,
  isImporting,
  isDarkMode,
  result,
  trackKey = 'investors',
  entityNoun = 'registros',
}) => {
  const isFund = trackKey === 'public_funds';
  const headers = isFund
    ? [...ENTITY_HEADERS_BASE.slice(0, -1), 'agency', 'instrument', 'application_deadline', 'created_by']
    : ENTITY_HEADERS_BASE;
  const labels = {
    name: 'Nombre',
    business_name: 'Razón social',
    rut: 'RUT',
    entity_type: 'Tipo',
    email: 'Email',
    phone: 'Teléfono',
    address: 'Dirección',
    commune: 'Comuna',
    region: 'Región',
    country: 'País',
    funnel_stage: 'Etapa',
    status: 'Estado',
    target_amount: 'Monto objetivo',
    committed_amount: 'Monto comprometido',
    agency: 'Agencia',
    instrument: 'Instrumento',
    application_deadline: 'Plazo',
    created_by: 'Creado por',
  };
  const parseFn = useMemo(
    () => (raw) => parseGenericCsv(raw, headers, ENTITY_ALIASES, 'name', 'name / nombre'),
    [headers]
  );
  return (
    <BulkShell
      isOpen={isOpen}
      onClose={onClose}
      isImporting={isImporting}
      isDarkMode={isDarkMode}
      title={`Carga masiva de ${entityNoun}`}
      subtitle="Duplicados por RUT o nombre se omiten al importar."
      headers={headers}
      labels={labels}
      parseFn={parseFn}
      onImport={onImport}
      result={result}
      successNoun={entityNoun}
    />
  );
};

export const FinancingContactsBulkImportModal = ({
  isOpen,
  onClose,
  onImport,
  isImporting,
  isDarkMode,
  result,
  parentLabel = 'entidad',
}) => {
  const headers = ['full_name', 'role', 'email', 'phone', 'parent_name', 'status', 'created_by'];
  const labels = {
    full_name: 'Nombre',
    role: 'Cargo',
    email: 'Correo',
    phone: 'Teléfono',
    parent_name: parentLabel,
    status: 'Estado',
    created_by: 'Creado por',
  };
  const aliases = {
    full_name: ['full_name', 'nombre', 'contacto'],
    role: ['role', 'cargo'],
    email: ['email', 'correo'],
    phone: ['phone', 'telefono', 'fono'],
    parent_name: ['parent_name', 'proveedor', 'inversionista', 'fondo', 'nombre_padre', 'entidad'],
    status: ['status', 'estado'],
    created_by: ['created_by', 'creado_por'],
  };
  const parseFn = useMemo(
    () => (raw) => parseGenericCsv(raw, headers, aliases, 'full_name', 'full_name / nombre'),
    []
  );
  return (
    <BulkShell
      isOpen={isOpen}
      onClose={onClose}
      isImporting={isImporting}
      isDarkMode={isDarkMode}
      title="Carga masiva de contactos"
      subtitle={`Asocia cada contacto por el nombre del ${parentLabel.toLowerCase()}.`}
      headers={headers}
      labels={labels}
      parseFn={parseFn}
      onImport={onImport}
      result={result}
      successNoun="contacto(s)"
    />
  );
};
